import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { BaseComponent } from '@shared/components/base/base.component';
import { withDialogComponent } from '@shared/components/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AssistantChatDialogComponent } from '../../../../features/assistant/dialogs/assistant-chat/assistant-chat-dialog.component';
import { IconButtonDirective } from '@shared/components/button/icon/icon-button.directive';
import { HeaderComponent } from '@shared/components/header/header.component';
import { NAVIGATION } from '@shared/tokens/navigation.token';
import { HeaderNavigationComponent } from '@shared/components/header/navigation/header-navigation.component';
import { CURRENT_USER } from '@shared/tokens/current-user.token';
import { LanguagePickerComponent } from '@shared/components/language-picker/language-picker.component';
import { ThemePickerComponent } from '@shared/components/theme-picker/theme-picker.component';
import { ProfileMenuComponent } from '@shared/components/profile-menu/profile-menu.component';
import { NotificationItem, NotificationPanelComponent } from '@shared/components/notification-panel/notification-panel.component';
import { PropertySelectComponent } from "../../../../shared/components/entity/select/property/property-select.component";
import { UserNotification } from '../../../../shared/models/entity/user-notification/user-notification.model';
import { UserNotificationsService } from '../../../../shared/services/entity/user-notifications.service';
import { RealtimeService } from '../../../../shared/services/local/realtime.service';

@Component({
  selector: 'backoffice-base-layout-header',
  imports: [
    CommonModule,
    HeaderComponent,
    HeaderNavigationComponent,
    IconButtonDirective,
    RouterLink,
    ProfileMenuComponent,
    NotificationPanelComponent,
    ThemePickerComponent,
    LanguagePickerComponent,
    PropertySelectComponent,
    TranslateModule,
],
  templateUrl: './base-layout-header.component.html',
  styleUrl: './base-layout-header.component.css',
})
export class BaseLayoutHeaderComponent extends withDialogComponent(BaseComponent) implements OnInit {
  // -----------------------------------------------------------------------------------------------------
  // @ DEPENDENCIES
  // -----------------------------------------------------------------------------------------------------

  readonly navigation = inject(NAVIGATION);

  readonly user = inject(CURRENT_USER);

  private readonly t = inject(TranslateService);

  private readonly router = inject(Router);

  private readonly notificationsService = inject(UserNotificationsService);

  private readonly realtime = inject(RealtimeService);

  // -----------------------------------------------------------------------------------------------------
  // @ INSTANCE VARIABLES
  // -----------------------------------------------------------------------------------------------------

  /** The latest in-app notifications of the current user, mapped for the bell panel. */
  readonly notificationItems = signal<NotificationItem[]>([]);

  /** The loaded rows by id — the panel item carries only the display shape. */
  private notificationsById = new Map<string, UserNotification>();

  /** Coalesces realtime bursts into one refetch. */
  private reloadTimer: ReturnType<typeof setTimeout> | null = null;

  // -----------------------------------------------------------------------------------------------------
  // @ LIFECYCLE HOOKS
  // -----------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    this.loadNotifications();

    // Live bell: notification rows are written by task actions and the digest job —
    // any task event or the explicit nudge refreshes the badge
    const unsubscribe = this.realtime.on('*', (event) => {
      if (!event.type.startsWith('task.') && event.type !== 'notification.created') {
        return;
      }

      if (this.reloadTimer) {
        clearTimeout(this.reloadTimer);
      }

      this.reloadTimer = setTimeout(() => {
        this.reloadTimer = null;
        this.loadNotifications();
      }, 2000);
    });

    // Fallback poll — the digest job writes without a user action in the tenant
    const pollTimer = setInterval(() => this.loadNotifications(), 120_000);

    this.destroyRef.onDestroy(() => {
      unsubscribe();
      clearInterval(pollTimer);

      if (this.reloadTimer) {
        clearTimeout(this.reloadTimer);
      }
    });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PUBLIC METHODS
  // -----------------------------------------------------------------------------------------------------

  /** Opens the global KI assistant — the email chat's tool loop without an email. */
  openAssistant(): void {
    this.openDialog(
      {
        header: this.t.instant('Assistant.Title'),
        content: AssistantChatDialogComponent,
      },
      { width: '44rem', maxWidth: '95vw' },
    );
  }

  /**
   * A notification was activated: mark it read and jump to its entity (tasks open their
   * dialog through the board's ?task= deep link).
   *
   * @param item - The activated panel item.
   * @returns {void}
   */
  openNotification(item: NotificationItem): void {
    const notification = this.notificationsById.get(item.id);

    if (this.IsNullOrUndefined(notification)) {
      return;
    }

    this.notificationsService.markRead(notification.id).subscribe(() => this.loadNotifications());

    if (notification.entityType === 'Task' && notification.entityId) {
      this.router.navigate(['/tasks'], { queryParams: { task: notification.entityId } });
    } else if (notification.entityType === 'Document' && notification.entityId) {
      this.router.navigate(['/documents', notification.entityId]);
    }
  }

  /** Marks the whole inbox read. */
  markAllNotificationsRead(): void {
    this.notificationsService.markAllRead().subscribe(() => this.loadNotifications());
  }

  /** "Alle anzeigen" — the board is where the notified work lives. */
  viewAllNotifications(): void {
    this.router.navigate(['/tasks']);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PRIVATE METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Loads the latest notifications and maps them onto the generic panel item shape.
   *
   * @returns {void}
   */
  private loadNotifications(): void {
    this.notificationsService.getAll(false, 30).subscribe((notifications) => {
      this.notificationsById = new Map(notifications.map((notification) => [notification.id, notification]));

      this.notificationItems.set(notifications.map((notification) => ({
        id: notification.id,
        title: notification.title,
        address: notification.body ?? '',
        channel: this.t.instant(`UserNotifications.Types.${notification.type}`),
        author: notification.actorName ?? '',
        read: !notification.isUnread,
      })));
    });
  }
}
